import { useEffect, useRef, useState } from 'react';

/**
 * Renders the race clock smoothly at frame rate while the server remains the
 * source of truth.
 *
 * The server ticks at ~10Hz. Displaying those samples directly would visibly
 * stutter. So we take each authoritative sample as an anchor and extrapolate
 * forward with the local clock until the next one lands — a 100ms window in
 * which the local clock cannot drift meaningfully.
 *
 * The client never *computes* remaining time from its own clock alone: every
 * sample re-anchors it. So this is presentation, not authority.
 */
export function useSmoothClock(): {
  remainingMs: number;
  sync: (serverRemainingMs: number) => void;
  reset: () => void;
} {
  const [remainingMs, setRemainingMs] = useState(0);
  // The last authoritative sample and when we received it (local monotonic).
  const anchor = useRef<{ remaining: number; at: number } | null>(null);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const tick = (): void => {
      const a = anchor.current;
      if (a) {
        const elapsed = performance.now() - a.at;
        setRemainingMs(Math.max(0, a.remaining - elapsed));
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, []);

  return {
    remainingMs,
    sync: (serverRemainingMs: number) => {
      anchor.current = { remaining: serverRemainingMs, at: performance.now() };
    },
    reset: () => {
      anchor.current = null;
      setRemainingMs(0);
    },
  };
}

/** `1:47` — the mockups' clock format. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** `71.4s` — the result screen's format. */
export function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}`;
}
